import { createElement, use } from 'react';
import type { ComponentType } from 'react';

// Components that live in their own chunk: MDX bodies (src/content/index.ts)
// and tool widgets (src/tools/widgets.tsx). Each one registers the route that
// shows it, and entry-server.tsx and main.tsx call preloadRoute before they
// render. So the prerender and the hydration both find the chunk already
// loaded and never suspend, and the HTML is the same as when everything was
// in one bundle. On a client-side navigation the component suspends until its
// chunk arrives; the router runs navigations as transitions (main.tsx), so the
// old page stays up meanwhile.

interface Tracked<T> extends Promise<T> {
  status?: 'pending' | 'fulfilled' | 'rejected';
  value?: T;
  reason?: unknown;
}

/**
 * A component whose code loads on first render. The promise carries its own
 * status, which `use` reads synchronously once it has resolved, so a preloaded
 * component renders on the first pass.
 */
export function lazyComponent<P extends object>(
  load: () => Promise<ComponentType<P>>,
): { Component: ComponentType<P>; preload: () => Promise<ComponentType<P>> } {
  let pending: Tracked<ComponentType<P>> | undefined;
  const preload = (): Promise<ComponentType<P>> => {
    if (!pending) {
      const p: Tracked<ComponentType<P>> = load();
      p.status = 'pending';
      p.then(
        (value) => Object.assign(p, { status: 'fulfilled', value }),
        (reason) => Object.assign(p, { status: 'rejected', reason }),
      );
      pending = p;
    }
    return pending;
  };
  const Component = (props: P) => createElement(use(preload()), props);
  return { Component, preload };
}

const preloads = new Map<string, () => Promise<unknown>>();
const normal = (path: string) => path.replace(/\/+$/, '') || '/';

/** Records that the page at `path` shows a lazy component. */
export function registerPreload(path: string, preload: () => Promise<unknown>): void {
  preloads.set(normal(path), preload);
}

/** Loads the lazy component the route at `path` shows, if any. */
export function preloadRoute(path: string): Promise<unknown> {
  return preloads.get(normal(path))?.() ?? Promise.resolve();
}
